// Storage Quota Monitor - Track and manage storage usage
import storageHelper from './storageHelper.js';
import indexedDBAdapter from '../services/indexedDBAdapter.js';

class StorageMonitor {
  constructor() {
    this.thresholds = {
      warning: 0.8,    // 80% of quota
      critical: 0.95   // 95% of quota
    };
    this.callbacks = {
      warning: [],
      critical: []
    };
  }

  // Get detailed storage usage statistics
  async getStorageStats() {
    try {
      const info = await storageHelper.getStorageInfo();

      const stats = {
        backend: info.backend,
        quota: info.quota || 0,
        usage: info.usage || 0,
        available: info.available || 0,
        usagePercentage: info.quota ? (info.usage / info.quota) * 100 : 0,
        formattedUsage: this.formatBytes(info.usage || 0),
        formattedQuota: this.formatBytes(info.quota || 0),
        formattedAvailable: this.formatBytes(info.available || 0),
        isUnlimited: info.backend === 'IndexedDB'
      };

      // Add status indicators
      if (stats.usagePercentage >= this.thresholds.critical * 100) {
        stats.status = 'critical';
        stats.statusMessage = 'Storage almost full! Consider cleaning up old data.';
      } else if (stats.usagePercentage >= this.thresholds.warning * 100) {
        stats.status = 'warning';
        stats.statusMessage = 'Storage usage is high. Consider migrating to IndexedDB.';
      } else {
        stats.status = 'healthy';
        stats.statusMessage = 'Storage usage is normal.';
      }

      return stats;
    } catch (error) {
      console.error('Failed to get storage stats:', error);
      return {
        backend: 'unknown',
        quota: 0,
        usage: 0,
        available: 0,
        usagePercentage: 0,
        status: 'error',
        statusMessage: 'Unable to determine storage usage.',
        error: error.message
      };
    }
  }

  // Format bytes to human readable format
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Monitor storage usage and trigger callbacks
  async checkStorageThresholds() {
    const stats = await this.getStorageStats();

    if (stats.status === 'critical') {
      this.triggerCallbacks('critical', stats);
    } else if (stats.status === 'warning') {
      this.triggerCallbacks('warning', stats);
    }

    return stats;
  }

  // Register callback for storage threshold events
  onThreshold(type, callback) {
    if (this.callbacks[type]) {
      this.callbacks[type].push(callback);
    }
  }

  // Trigger callbacks for threshold events
  triggerCallbacks(type, stats) {
    this.callbacks[type].forEach(callback => {
      try {
        callback(stats);
      } catch (error) {
        console.error(`Storage monitor callback error (${type}):`, error);
      }
    });
  }

  // Get breakdown of storage usage by data type
  async getStorageBreakdown() {
    const breakdown = {
      workflows: { size: 0, count: 0 },
      videoPositions: { size: 0, count: 0 },
      preferences: { size: 0, count: 0 },
      enhancements: { size: 0, count: 0 },
      autosave: { size: 0, count: 0 },
      total: { size: 0, count: 0 }
    };

    try {
      // Check workflows
      const workflows = await indexedDBAdapter.getWorkflows();
      if (workflows.length > 0) {
        breakdown.workflows.count = workflows.length;
        breakdown.workflows.size = new Blob([JSON.stringify(workflows)]).size;
      }

      // Check video positions
      const videoKeys = await storageHelper.getAllVideoKeys();
      breakdown.videoPositions.count = videoKeys.length;
      for (const key of videoKeys) {
        const data = await storageHelper.getItem(key);
        if (data) {
          breakdown.videoPositions.size += new Blob([data]).size;
        }
      }

      // Check preferences
      const preferences = await indexedDBAdapter.getUIPreferences();
      if (preferences) {
        breakdown.preferences.count = 1;
        breakdown.preferences.size = new Blob([preferences]).size;
      }

      // Check enhancements
      const enhancements = await indexedDBAdapter.getEnhancementSettings();
      if (enhancements) {
        breakdown.enhancements.count = 1;
        breakdown.enhancements.size = new Blob([enhancements]).size;
      }

      // Calculate totals
      Object.keys(breakdown).forEach(key => {
        if (key !== 'total') {
          breakdown.total.size += breakdown[key].size;
          breakdown.total.count += breakdown[key].count;
        }
      });

      // Add formatted sizes
      Object.keys(breakdown).forEach(key => {
        breakdown[key].formattedSize = this.formatBytes(breakdown[key].size);
      });

      return breakdown;
    } catch (error) {
      console.error('Failed to get storage breakdown:', error);
      return breakdown;
    }
  }

  // Clean up old or unnecessary data
  async performCleanup(options = {}) {
    const {
      removeOldVideos = true,
      daysToKeep = 7,
      removeAutosave = false
    } = options;

    const cleaned = {
      videoPositions: 0,
      autosave: 0,
      bytesFreed: 0
    };

    try {
      // Clean up old video positions
      if (removeOldVideos) {
        const videoKeys = await storageHelper.getAllVideoKeys();
        const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);

        for (const key of videoKeys) {
          try {
            const data = JSON.parse(await storageHelper.getItem(key));
            if (data.timestamp && data.timestamp < cutoffTime) {
              const size = new Blob([JSON.stringify(data)]).size;
              await storageHelper.removeItem(key);
              cleaned.videoPositions++;
              cleaned.bytesFreed += size;
            }
          } catch (e) {
            // Remove corrupted entries
            await storageHelper.removeItem(key);
            cleaned.videoPositions++;
          }
        }
      }

      // Clean up autosave data if requested
      if (removeAutosave) {
        try {
          await storageHelper.removeItem('bible_workflow_autosave');
          cleaned.autosave++;
        } catch (e) {
          // Ignore if doesn't exist
        }
      }

      // console.log('🧹 Storage cleanup completed:', cleaned);
      return cleaned;
    } catch (error) {
      console.error('Storage cleanup failed:', error);
      return cleaned;
    }
  }

  // Start periodic monitoring
  startMonitoring(intervalMinutes = 30) {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    this.monitoringInterval = setInterval(async () => {
      await this.checkStorageThresholds();
    }, intervalMinutes * 60 * 1000);

    // console.log(`📊 Storage monitoring started (checking every ${intervalMinutes} minutes)`);
  }

  // Stop periodic monitoring
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
      // console.log('📊 Storage monitoring stopped');
    }
  }

  // Check if IndexedDB migration is recommended
  async shouldMigrateToIndexedDB() {
    const stats = await this.getStorageStats();

    return {
      recommended: stats.backend === 'localStorage' && stats.usagePercentage > 50,
      reason: stats.backend === 'localStorage' ?
        'Using localStorage with limited capacity' :
        'Already using IndexedDB',
      currentUsage: stats.usagePercentage,
      benefits: [
        'Unlimited storage capacity',
        'Better performance for large datasets',
        'Structured data organization',
        'Async operations'
      ]
    };
  }
}

export default new StorageMonitor();